import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Select from "react-select";
import toast from "react-hot-toast";
import { FiAlertTriangle, FiCheck, FiPlus, FiTrash2 } from "react-icons/fi";
import { formatNaira } from "../../../utils/dashboardFormat";
import { compactNaira } from "../../../components/MemberProfile/profileModel";
import {
  useCreateMemberContributionMutation,
  useGetContributionCategoriesQuery,
  useGetContributionMemberSummaryQuery,
} from "../../../store/admin/contribution";
import { useGetAllMembersQuery, useGetPaymentMethodsQuery } from "../../../store/selectableSlice";
import { listFrom, normalizeMemberCategories, whenLabel } from "../../../components/Contributions/contribModel";
import {
  buildPayload, categoryOptions, emptyLine, needsCode, standingFor, standingText, suggestedAmount, todayISO, totalOf, validateLines,
} from "../../../components/Contributions/recordModel";
import { PageHeader } from "../../../components/Contributions/parts";

const OVERVIEW = "/super_admin/contribution/contribution_history";
const selectStyles = {
  control: (base, state) => ({ ...base, minHeight: 40, borderRadius: 10, borderColor: state.selectProps["aria-invalid"] ? "#b42318" : "#e4e1e1", boxShadow: "none" }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};
const methodName = (m) => String(m?.name || m?.label || "Method").replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

function Field({ id, label, error, hint, children }) {
  return (
    <div className="cb-fieldgroup">
      <label htmlFor={id}>{label}</label>
      {children}
      {error ? (
        <p className="err" id={`${id}-err`} role="alert">{error}</p>
      ) : hint ? (
        <p className="hint" id={`${id}-hint`}>{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * Record one or more payments for a member: pick the member, add what they paid, check it, save.
 * Shows what each member still owes so staff are not guessing, and says what went wrong in plain words.
 */
export default function RecordPayment() {
  const { id: presetCategory } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [member, setMember] = useState(params.get("member") || "");
  const [lines, setLines] = useState(() => [emptyLine()]);
  const [step, setStep] = useState("form"); // form | review | done
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");
  const [saved, setSaved] = useState(null);

  const members = listFrom(useGetAllMembersQuery().data);
  const categoriesQuery = useGetContributionCategoriesQuery();
  const options = useMemo(() => categoryOptions(listFrom(categoriesQuery.data, "categories")), [categoriesQuery.data]);
  const methods = listFrom(useGetPaymentMethodsQuery().data, "payment_methods", "paymentMethods");
  const summaryQuery = useGetContributionMemberSummaryQuery(member, { skip: !member });
  const summary = summaryQuery.data || null;
  const standing = useMemo(() => (summary ? normalizeMemberCategories(summary) : null), [summary]);
  const [create, { isLoading: saving }] = useCreateMemberContributionMutation();


  const memberOptions = useMemo(
    () =>
      members
        .filter((m) => m && (m.id ?? m.member_id))
        .map((m) => ({
          value: String(m.id ?? m.member_id),
          label: [m.first_name, m.last_name].filter(Boolean).join(" ") || m.name || "Member",
          no: m.member_no,
        })),
    [members]
  );
  const memberLabel = memberOptions.find((o) => o.value === String(member))?.label || "";

  // Arriving from a category page: start with that category on the first line.
  useEffect(() => {
    if (presetCategory && options.some((o) => o.id === String(presetCategory))) {
      setLines((current) => (current.length === 1 && !current[0].category ? [{ ...current[0], category: String(presetCategory) }] : current));
    }
  }, [presetCategory, options]);

  const validation = validateLines(lines, { categories: options, summary, now: new Date() });
  const showErrors = submitted;
  const total = totalOf(lines);

  const setLine = (index, patch) => setLines((current) => current.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const pickCategory = (index, categoryId) => {
    const suggestion = suggestedAmount(summary, categoryId);
    setLines((current) => current.map((l, i) => (i === index ? { ...l, category: categoryId, amount: l.amount || suggestion } : l)));
  };
  const addLine = () => setLines((current) => [...current, emptyLine({ method: current[current.length - 1]?.method || "", date: current[current.length - 1]?.date || todayISO() })]);
  const removeLine = (index) => setLines((current) => current.filter((_, i) => i !== index));

  const goReview = () => {
    setSubmitted(true);
    setServerError("");
    if (!member || !validation.ok) return;
    setStep("review");
    window.scrollTo?.({ top: 0 });
  };

  const save = async () => {
    setServerError("");
    const response = await create(buildPayload(member, lines));
    if (response.error || response.data?.status === false) {
      setServerError(response.error?.data?.message || response.data?.message || "We couldn't save this. Check the details and try again.");
      return;
    }
    toast.success(response.data?.message || "Payment recorded");
    setSaved({ total, count: lines.length, memberId: member, name: memberLabel });
    setStep("done");
    window.scrollTo?.({ top: 0 });
  };

  const again = (sameMember) => {
    setLines([emptyLine()]);
    setSubmitted(false);
    setServerError("");
    setStep("form");
    if (!sameMember) setMember("");
  };

  if (step === "done" && saved) {
    return (
      <section className="cb" aria-label="Payment recorded">
        <div className="cb-card cb-done" role="status">
          <span className="ico"><FiCheck size={28} aria-hidden="true" /></span>
          <h1 className="cb-title">Payment recorded</h1>
          <p className="cb-mute">
            {formatNaira(saved.total)} recorded for {saved.name}
            {saved.count > 1 ? ` across ${saved.count} categories` : ""}.
          </p>
          <div className="row">
            <button type="button" className="cb-btn primary" onClick={() => again(true)}>Record another for {saved.name.split(" ")[0] || "this member"}</button>
            <button type="button" className="cb-btn" onClick={() => again(false)}>Record for someone else</button>
          </div>
          <div className="row">
            <Link to={`/super_admin/contribution/contribution_history_v2?member=${saved.memberId}`} className="cb-btn sm">See in All payments</Link>
            <Link to={OVERVIEW} className="cb-btn sm">Totals by category</Link>
          </div>
        </div>
      </section>
    );
  }

  if (step === "review") {
    return (
      <section className="cb" aria-label="Check the payment">
        <PageHeader title="Check before saving" sub="Make sure this is right. You can go back and change it." />
        <div className="cb-card">
          <p className="cb-mute">Recording for</p>
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>{memberLabel}</h2>
          <div className="cb-scroll">
            <table className="cb-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Method</th>
                  <th>Date</th>
                  <th>Reference</th>
                  <th className="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td>{options.find((o) => o.id === l.category)?.name || "—"}</td>
                    <td>{methodName(methods.find((m) => String(m.id ?? m.value) === String(l.method)))}</td>
                    <td>{whenLabel(l.date)}</td>
                    <td className="cb-ref">{needsCode(l.method) ? l.code : "—"}</td>
                    <td className="num" style={{ fontWeight: 600, color: "var(--cb-ink)" }}>{formatNaira(Number(String(l.amount).replace(/,/g, "")))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {serverError ? (
            <div className="cb-banner tone-red" role="alert" style={{ marginTop: 12 }}>
              <FiAlertTriangle size={16} aria-hidden="true" />
              <span>{serverError}</span>
            </div>
          ) : null}
        </div>
        <div className="cb-sticky">
          <div>
            <span className="cb-faint">Total</span>
            <div className="total">{formatNaira(total)}</div>
          </div>
          <div className="cb-actions">
            <button type="button" className="cb-btn" onClick={() => { setStep("form"); setServerError(""); }}>Back to edit</button>
            <button type="button" className="cb-btn primary" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save payment"}</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="cb" aria-label="Record a payment">
      <PageHeader title="Record a payment" sub="One member, one or more categories. Check it on the next step before it's saved." backTo={OVERVIEW} backLabel="Totals by category" />

      <div className="cb-card" data-tour="staff-contrib-member">
        <div className="cb-step"><span className="n">1</span><h2>Who paid?</h2></div>
        <Field id="member" label="Member" error={showErrors && !member ? "Choose a member" : ""} hint="Search by name">
          <Select
            inputId="member"
            aria-label="Member"
            aria-invalid={showErrors && !member}
            placeholder="Search for a member"
            options={memberOptions}
            value={memberOptions.find((o) => o.value === String(member)) || null}
            onChange={(o) => setMember(o?.value || "")}
            isClearable
            isSearchable
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            styles={selectStyles}
            formatOptionLabel={(o) => (
              <span>
                {o.label}
                {o.no ? <span style={{ color: "#8a8a90", fontSize: 12 }}> #{o.no}</span> : null}
              </span>
            )}
          />
        </Field>
        {member && standing ? (
          <div className="cb-snapshot" aria-label={`${memberLabel} so far`}>
            <div><b>{compactNaira(standing.totals.total)}</b><span>Total contributed</span></div>
            <div><b>{compactNaira(standing.totals.savings)}</b><span>Savings</span></div>
            <div><b>{compactNaira(standing.totals.shares)}</b><span>Shares</span></div>
            {standing.pendingCount ? <div><b>{standing.pendingCount}</b><span>Waiting for confirmation</span></div> : null}
          </div>
        ) : null}
      </div>

      <div className="cb-card">
        <div className="cb-step"><span className="n">2</span><h2>What did they pay?</h2></div>
        {options.length === 0 && !categoriesQuery.isLoading ? (
          <div className="cb-banner tone-amber" role="alert">
            <FiAlertTriangle size={16} aria-hidden="true" />
            <span>There are no categories open for payments today. Add one under <Link to="/super_admin/contribution/contribution-category">Categories</Link>.</span>
          </div>
        ) : null}

        <div className="cb-lines">
          {lines.map((line, i) => {
            const err = showErrors ? validation.errors[i] : {};
            const st = line.category ? standingFor(summary, line.category) : null;
            const cat = options.find((o) => o.id === line.category);
            const hint = st ? standingText(st) : cat?.perMember ? `${formatNaira(cat.perMember)} per member` : cat?.max ? `Up to ${formatNaira(cat.max)}` : "";
            const idp = `line${i}`;
            return (
              <div className="cb-line" key={i} role="group" aria-label={`Payment ${i + 1}`}>
                <div className="top">
                  <span>Payment {i + 1}</span>
                  {lines.length > 1 ? (
                    <button type="button" className="cb-btn sm" onClick={() => removeLine(i)} aria-label={`Remove payment ${i + 1}`}>
                      <FiTrash2 size={14} aria-hidden="true" /> Remove
                    </button>
                  ) : null}
                </div>
                <div className="cb-grid two">
                  <Field id={`${idp}-cat`} label="Category" error={err.category} hint={hint}>
                    <select id={`${idp}-cat`} className={`cb-field ${err.category ? "bad" : ""}`} value={line.category} onChange={(e) => pickCategory(i, e.target.value)} aria-invalid={Boolean(err.category)}>
                      <option value="">Choose a category</option>
                      {options.map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  </Field>
                  <Field id={`${idp}-amt`} label="Amount" error={err.amount} hint={Number(String(line.amount).replace(/,/g, "")) > 0 ? formatNaira(Number(String(line.amount).replace(/,/g, ""))) : "In naira"}>
                    <input id={`${idp}-amt`} className={`cb-field ${err.amount ? "bad" : ""}`} inputMode="decimal" placeholder="0.00" value={line.amount} onChange={(e) => setLine(i, { amount: e.target.value })} aria-invalid={Boolean(err.amount)} />
                  </Field>
                  <Field id={`${idp}-method`} label="How was it paid?" error={err.method}>
                    <select id={`${idp}-method`} className={`cb-field ${err.method ? "bad" : ""}`} value={line.method} onChange={(e) => setLine(i, { method: e.target.value })} aria-invalid={Boolean(err.method)}>
                      <option value="">Choose a method</option>
                      {methods.map((m) => (
                        <option key={m.id ?? m.value} value={m.id ?? m.value}>{methodName(m)}</option>
                      ))}
                    </select>
                  </Field>
                  <Field id={`${idp}-date`} label="Payment date" error={err.date}>
                    <input id={`${idp}-date`} type="date" className={`cb-field ${err.date ? "bad" : ""}`} value={line.date} max={todayISO()} onChange={(e) => setLine(i, { date: e.target.value })} aria-invalid={Boolean(err.date)} />
                  </Field>
                  {needsCode(line.method) ? (
                    <Field id={`${idp}-code`} label="Reference or cheque number" error={err.code} hint="Printed on the receipt, cheque or transfer slip">
                      <input id={`${idp}-code`} className={`cb-field ${err.code ? "bad" : ""}`} value={line.code} onChange={(e) => setLine(i, { code: e.target.value })} aria-invalid={Boolean(err.code)} />
                    </Field>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 12 }}>
          <button type="button" className="cb-btn" onClick={addLine}>
            <FiPlus size={16} aria-hidden="true" /> Add another category
          </button>
        </div>
      </div>

      <div className="cb-sticky">
        <div>
          <span className="cb-faint">{lines.length} {lines.length === 1 ? "payment" : "payments"}</span>
          <div className="total" aria-live="polite">{formatNaira(total)}</div>
        </div>
        <div className="cb-actions">
          <button type="button" className="cb-btn" onClick={() => navigate(-1)}>Cancel</button>
          <button type="button" className="cb-btn primary" onClick={goReview}>Review and save</button>
        </div>
      </div>
    </section>
  );
}
